
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LogOut, Search, User, CalendarDays, Shield, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tables } from "@/integrations/supabase/types";
import { SearchUsers } from "@/components/SearchUsers";
import { useState } from "react";
import { NotificationDropdown } from "@/components/notifications/NotificationDropdown";
import { getAvatarUrl } from "@/utils/storageUtils";
import { useViewMode } from "@/contexts/ViewModeContext";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface DashboardHeaderProps {
  profile: Tables<"profiles">;
  onSignOut: () => void;
  isAdmin?: boolean;
}

export const DashboardHeader = ({ profile, onSignOut, isAdmin }: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const avatarUrl = getAvatarUrl(profile?.avatar_url);
  const { viewMode, setViewMode, isViewingAsUser } = useViewMode();
  const effectiveIsAdmin = isAdmin && !isViewingAsUser;
  console.log('DashboardHeader - Avatar URL:', avatarUrl);

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3">
          <img 
            src="/lovable-uploads/d552ed3f-b5c5-4bef-b2b2-d29fbfb5238b.png" 
            alt="Scheduler Logo" 
            className="h-8 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowSearch(!showSearch)}
              className="hover:bg-gray-100"
            >
              <Search className="h-5 w-5" />
            </Button>
            <NotificationDropdown />
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/profile")}
              className="hover:bg-gray-100"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage 
                  src={avatarUrl}
                  alt={profile?.full_name || 'User'}
                />
                <AvatarFallback>
                  {profile?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                </AvatarFallback>
              </Avatar>
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onSignOut}
              className="hover:bg-gray-100"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {showSearch && (
          <div className="lg:hidden px-4 py-2 border-t border-gray-100">
            <SearchUsers />
          </div>
        )}

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/d552ed3f-b5c5-4bef-b2b2-d29fbfb5238b.png" 
                alt="Scheduler Logo" 
                className="h-[3.6rem] w-auto cursor-pointer"
                onClick={() => navigate("/")}
              />
            </div>
            <SearchUsers />
          </div>
          <div className="flex items-center space-x-4">
            <NotificationDropdown />
            
            {isAdmin ? (
              <HoverCard openDelay={100} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <Avatar
                    className="cursor-pointer h-10 w-10 border-2 border-white/30"
                    onClick={() => navigate("/profile")}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full" />
                    <AvatarImage 
                      src={avatarUrl}
                      alt={profile?.full_name || 'User'} 
                      className="object-cover relative z-10"
                    />
                    <AvatarFallback className="text-sm text-muted-foreground relative z-10">
                      {profile?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </HoverCardTrigger>
                <HoverCardContent className="w-48 p-2" align="end">
                  <div className="space-y-1">
                    <button
                      onClick={() => setViewMode("admin")}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        viewMode === "admin" 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-accent text-foreground"
                      }`}
                    >
                      <Shield className="h-4 w-4" />
                      View as Admin
                    </button>
                    <button
                      onClick={() => setViewMode("user")}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        viewMode === "user" 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-accent text-foreground"
                      }`}
                    >
                      <UserRound className="h-4 w-4" />
                      View as User
                    </button>
                  </div>
                </HoverCardContent>
              </HoverCard>
            ) : (
              <Avatar
                className="cursor-pointer h-10 w-10 border-2 border-white/30"
                onClick={() => navigate("/profile")}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full" />
                <AvatarImage 
                  src={avatarUrl}
                  alt={profile?.full_name || 'User'} 
                  className="object-cover relative z-10"
                />
                <AvatarFallback className="text-sm text-muted-foreground relative z-10">
                  {profile?.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                </AvatarFallback>
              </Avatar>
            )}
            <Button variant="ghost" size="icon" onClick={onSignOut}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
